import { ChatCopyInfo } from '@agent/agent-interface/contact-view/chat/model/chat-copy-info';
import { ChatCopyMessage } from '@agent/agent-interface/contact-view/chat/model/chat-copy-message';
import { MessageType } from '@agent/agent-interface/contact-view/chat/model/message-type';
import { SystemMessageType } from '@agent/agent-interface/contact-view/chat/model/system-message-type';
import { ChatAttachmentService, MAX_ATTACHMENT_COUNT } from '@agent/agent-interface/contact-view/chat/services/chat-attachment.service';
import { ChatCopyService } from '@agent/agent-interface/contact-view/chat/services/chat-copy.service';
import { ChatEmojiService } from '@agent/agent-interface/contact-view/chat/services/chat-emoji.service';
import { ChatKnowledgeService } from '@agent/agent-interface/contact-view/chat/services/chat-knowledge.service';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChatCopyGuest } from '@commons/chat-assistance/models/chat-copy-guest';
import { debounceTime, fromEvent, Subscription, tap } from 'rxjs';

import { ProfileService, randomUUID } from '@contact/upgrade';

import { ChatMessage } from '../../model/chat-message';
import { ChatModel } from '../../model/chat-model';
import {
  ChatSocketService,
  MESSAGE_ENDPOINT,
  SYSTEM_MESSAGE_ENDPOINT,
  TYPE_END_DEBOUNCE_TIME,
  TYPE_PENDING_DEBOUNCE_TIME,
} from '../../services/chat-socket.service';

@Component({
  selector: 'chat-box',
  templateUrl: 'chat-box.component.html',
  styleUrls: ['./chat-box.component.scss'],
})
export class ChatBoxComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input()
  public chat: ChatModel;

  @Input()
  public extendedMirror: boolean = false;

  @ViewChild('chatListChanged')
  private _chatListElement: ElementRef;

  @ViewChild('chatTextArea')
  private _chatTextAreaElement: ElementRef;

  @ViewChild('chatTyping')
  private _chatTyping: ElementRef;

  @ViewChild('fileInput')
  private _fileInputElement: ElementRef | undefined;

  public messageBoxText: string = '';
  public showSmileArea: boolean = false;
  public messageToCopy: ChatMessage | undefined = undefined;
  private subscriptions: Subscription[] = [];
  public enableSmiley: boolean = false;
  public canSendAttachment: boolean = false;
  public displayGlobalCopyLayer: boolean = false;
  public displayGlobalAttachmentLayer: boolean = false;

  public reduced: boolean = false;
  private attachmentExtensions: string;

  private _countChatMessages: number = 0;
  private _lastContentChatMessage: string = '';
  private lastChatTypingHeight: number = 0;
  constructor(
    public readonly chatSocketService: ChatSocketService,
    private readonly copyService: ChatCopyService,
    protected readonly profileService: ProfileService,
    private readonly chatEmojiService: ChatEmojiService,
    protected readonly chatAttachmentService: ChatAttachmentService,
    private readonly chatKnowledgeService: ChatKnowledgeService
  ) {}

  ngOnInit() {
    this.showSmileArea = false;

    this.profileService.hasPermission$('CSR_CHAT_INCOMING::contact:use_smiley').subscribe(right => {
      this.enableSmiley = right;
    });

    this.profileService.hasPermission$('CSR_CHAT_INCOMING::contact:send_attachment').subscribe(right => {
      this.canSendAttachment = right;
    });

    let subscription = this.chatKnowledgeService.knowledgeBaseMessage$.subscribe(knowledgeData => {
      if (knowledgeData.responseId === this.chat.responseId) {
        this.setMessageToSend(knowledgeData.message.toString());
        if (knowledgeData.attachments && knowledgeData.attachments.length > 0) {
          this.chatAttachmentService.processFileList(knowledgeData.attachments, this.chat.customerId);
        }
      }
    });
    this.subscriptions.push(subscription);
    this.attachmentExtensions = this.chatAttachmentService.extensions.map(ex => '.' + ex).join(',');
  }

  ngAfterViewInit() {
    this.onTyping();
    setTimeout(() => {
      let messagesElement = document.getElementsByClassName('chat-history').item(0);
      if (messagesElement) {
        messagesElement.scrollTop = messagesElement.scrollHeight;
      }
    }, 500);
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get attachmentInfo() {
    return this.chatAttachmentService.getAttachmentInfo(this.chat.customerId);
  }

  get count() {
    return MAX_ATTACHMENT_COUNT;
  }

  get extensions() {
    return this.attachmentExtensions;
  }

  get conversationCloseByTheClient() {
    return this.chat && this.chat.tab.conversationEndByClient;
  }

  chatBoxKeyPressed(event) {
    // If the user has pressed Enter without a 'Shift' combination, send the message
    const ev = event.getModifierState ? event : event.originalEvent;
    if (ev.key === 'Enter' && !ev.getModifierState('Shift')) {
      ev.preventDefault();
      this.sendNewMessage();
    }
  }

  setMessageToSend(textToSend: string): void {
    if (this._chatTextAreaElement) {
      this._chatTextAreaElement.nativeElement.innerHTML = textToSend;
    }
  }

  sendNewMessage() {
    if (this._chatTextAreaElement) {
      this.messageBoxText = this._chatTextAreaElement.nativeElement.innerHTML;
      const textContent = this.replaceImageAttributes(this.messageBoxText).trim();
      if (textContent.length) {
        this.chatSocketService.send(MESSAGE_ENDPOINT, textContent);
        this.messageBoxText = '';
        this._chatTextAreaElement.nativeElement.innerHTML = '';
      }
      this.chatAttachmentService.sendFilesIfExist(this.chat.customerId);
    }
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = (target.files && Array.from(target.files)) ?? [];
    this.chatAttachmentService.processFileList(files, this.chat.customerId);
    target.value = '';
    this._fileInputElement && (this._fileInputElement.nativeElement.value = '');
  }

  fileCountExceeded(): boolean {
    const info = this.chatAttachmentService.getAttachmentInfo(this.chat.customerId);
    return info ? info.validatedFiles.length >= MAX_ATTACHMENT_COUNT : false;
  }

  removeFile(file) {
    this.chatAttachmentService.removeFile(file, this.chat.customerId);
  }

  removeWarning(error) {
    this.chatAttachmentService.removeWarning(error, this.chat.customerId);
  }

  onCancel(attachment) {
    this.chatAttachmentService.cancelUpload(attachment, this.chat.customerId);
  }

  onUpload(attachment) {
    this.chatAttachmentService.retryUpload(attachment, this.chat.customerId);
  }

  /**
   * get images and replace with alt names for example :sunny:
   * @param textContent
   */
  private replaceImageAttributes(textContent: string) {
    return textContent.replace(/<img.*?alt="(.*?)"[^\>]+>/g, x => {
      let div = document.createElement('div');
      div.innerHTML = x;
      return ':' + div.getElementsByTagName('img')[0].alt + ':';
    });
  }

  copyToRoom(room) {
    const message = room.message;
    const emojifiedContent = this.replaceImageAttributes(message.content);
    const copyMessage = new ChatCopyMessage(
      emojifiedContent,
      new ChatCopyGuest(message.firstName, message.lastName, message.date, message.isFromCustomer())
    );
    const copyInfo = new ChatCopyInfo(room.roomId, [copyMessage]);
    this.copy(copyInfo);
    this.messageToCopy = undefined;
  }

  copyAllMessagesToRoom(roomId: string) {
    if (this.chatSocketService.activeChat) {
      const copyMessages = this.chatSocketService.activeChat.messages
        .filter(msg => msg.type === MessageType.CHAT || msg.type === MessageType.CHAT_AUTOMATIC_MESSAGE)
        .map(
          msg =>
            new ChatCopyMessage(
              this.chatEmojiService.emojify(msg.content),
              new ChatCopyGuest(msg.firstName, msg.lastName, msg.date, msg.isFromCustomer())
            )
        );
      const copyInfo = new ChatCopyInfo(roomId, copyMessages);
      this.copy(copyInfo);
    }
    this.displayGlobalCopyLayer = false;
  }

  burgerClick(message: ChatMessage) {
    this.messageToCopy = message;
  }

  globalBurgerClick() {
    this.displayGlobalCopyLayer = !this.displayGlobalCopyLayer;
    if (this.displayGlobalAttachmentLayer) {
      this.displayGlobalAttachmentLayer = false;
    }
  }

  globalAttachmentClick() {
    this.displayGlobalAttachmentLayer = !this.displayGlobalAttachmentLayer;
    if (this.displayGlobalCopyLayer) {
      this.displayGlobalCopyLayer = false;
    }
  }

  getAttachmentList(): ChatMessage[] {
    return this.chat.messages.filter(msg => msg.type === MessageType.FILE);
  }

  downloadFile(msg: ChatMessage) {
    if (msg.attachment) {
      this.chatAttachmentService
        .downloadFile(msg.attachment.fileDownloadUri)
        .subscribe(res => this.chatAttachmentService.downloadBlob(res, (msg.attachment && msg.attachment.fileName) || ''));
    }
  }

  private copy(copyInfo: ChatCopyInfo) {
    copyInfo.copyKey = randomUUID();
    copyInfo.copyMessages.push(new ChatCopyMessage('', new ChatCopyGuest('', ''), true));
    this.copyService.copy(copyInfo);
  }

  private onTyping() {
    if (this._chatTextAreaElement) {
      let subscription = fromEvent(this._chatTextAreaElement.nativeElement, 'input')
        .pipe(
          debounceTime(TYPE_PENDING_DEBOUNCE_TIME),
          tap(() => {
            this.messageBoxText = this._chatTextAreaElement.nativeElement.innerHTML;
            if (this.messageBoxText.trim()) {
              this.chatSocketService.sendSystemMessageToAgent(SYSTEM_MESSAGE_ENDPOINT, SystemMessageType.TYPING, this.messageBoxText);
            }
          }),
          debounceTime(TYPE_END_DEBOUNCE_TIME),
          tap(() => {
            this.messageBoxText = this._chatTextAreaElement.nativeElement.innerHTML;
            this.chatSocketService.sendSystemMessageToAgent(SYSTEM_MESSAGE_ENDPOINT, SystemMessageType.TYPING_END, this.messageBoxText);
          })
        )
        .subscribe();
      this.subscriptions.push(subscription);
    }
  }

  selectEmoji(emoji: any) {
    if (this._chatTextAreaElement) {
      this._chatTextAreaElement.nativeElement.focus();
      this.pasteHtmlAtCaret(emoji);
      this._chatTextAreaElement.nativeElement.blur();
      this._chatTextAreaElement.nativeElement.focus();
    }
  }

  handlePastAction(event) {
    this.pasteHtmlAtCaret(this.getReplacedContent(event));
  }

  private pasteHtmlAtCaret(html) {
    let sel;
    let range;
    if (window.getSelection) {
      // IE9 and non-IE
      sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        range = sel.getRangeAt(0);
        range.deleteContents();

        const el = document.createElement('div');
        el.innerHTML = html;
        const frag = document.createDocumentFragment();
        let node = el.firstChild;
        if (node) {
          const lastNode = frag.appendChild(node);
          range.insertNode(frag);

          if (lastNode) {
            range = range.cloneRange();
            range.setStartAfter(lastNode);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
    } else if ((document as any).selection && (document as any).selection.type !== 'Control') {
      // IE < 9
      (document as any).selection.createRange().pasteHTML(html);
    }
  }

  private getReplacedContent(event: any): string {
    event.preventDefault();
    if (this._chatTextAreaElement) {
      let pastedText = this._chatTextAreaElement.nativeElement.innerHTML;
      if ((window as any).clipboardData) {
        pastedText = (window as any).clipboardData.getData('text');
      } else if (event.clipboardData) {
        pastedText = event.clipboardData.getData('text');
      }

      // replace pasted html with plain text
      return pastedText.replace(/<(.|\n)*?>/g, '');
    }
    return '';
  }

  scrollToBottom() {
    const isContactChangeTyping =
      this._chatTyping &&
      this._chatTyping.nativeElement &&
      this._chatTyping.nativeElement.firstElementChild &&
      this._chatTyping.nativeElement.firstElementChild.firstElementChild &&
      this._chatTyping.nativeElement.firstElementChild.firstElementChild.children.length === 1;

    const chatTypingHeight = this._chatTyping ? this._chatTyping.nativeElement.clientHeight : 0;
    const offsetTyping = chatTypingHeight - this.lastChatTypingHeight;

    if (this._chatListElement && this.chatSocketService.activeChat) {
      const messages = this.chatSocketService.activeChat.messages;
      if (
        messages &&
        messages.length > 0 &&
        (this._countChatMessages !== messages.length || this._lastContentChatMessage !== messages[messages.length - 1].content)
      ) {
        this._chatListElement.nativeElement.scrollTop = this._chatListElement.nativeElement.scrollHeight;
        this._countChatMessages = messages.length;
        this._lastContentChatMessage = messages[messages.length - 1].content;
      }
    }
    if (isContactChangeTyping) {
      if (offsetTyping !== 0) {
        this._chatListElement.nativeElement.scrollTop = this._chatListElement.nativeElement.scrollTop + offsetTyping;
      }
      this.lastChatTypingHeight = chatTypingHeight;
    } else if (chatTypingHeight === 0) {
      this.lastChatTypingHeight = chatTypingHeight;
    }
  }
}
